import { Box, Typography } from '@mui/material';

import type { AgingAmountBasis, IAgingBucketData } from 'src/types';
import { fCurrency } from 'src/utils/format-number';
import { useLocales } from 'src/locales';

type Props = {
  basisSplit?: Record<AgingAmountBasis, IAgingBucketData>;
};

/**
 * NAT-2261: the bucket totals mix two different things — invoices actually issued, and orders
 * flagged for invoicing whose document does not exist yet. Both belong on the page, but a
 * forecast passing silently as a receivable is what that ticket set out to stop, so the split is
 * stated under the cards rather than left for the reader to assume. NAT-2610 moved it here so
 * Operativa and the «Da incassare» page state it the same way.
 */
export default function ReceivablesBasisSplit({ basisSplit }: Props) {
  const { t, currentLang } = useLocales('operativaDashboard');
  const invoice = basisSplit?.invoice;
  const forecast = basisSplit?.order_estimate;
  const excluded = basisSplit?.excluded;

  if (!invoice || !forecast) return null;

  return (
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {t('di_cui_fatturato')}{' '}
      <Box component="span" sx={{ fontWeight: 'fontWeightMedium', color: 'text.primary' }}>
        {fCurrency(invoice.amount.toFixed(2), currentLang.value)}
      </Box>{' '}
      ({invoice.count}) · {t('da_emettere')}{' '}
      <Box component="span" sx={{ fontWeight: 'fontWeightMedium', color: 'text.primary' }}>
        {fCurrency(forecast.amount.toFixed(2), currentLang.value)}
      </Box>{' '}
      ({forecast.count})
      {excluded?.count ? (
        <>
          {' '}
          · {t('non_conteggiate')} ({excluded.count})
        </>
      ) : null}
    </Typography>
  );
}
